import React, { useCallback, useEffect, useState } from "react";
import { useAuthContext } from "../../context/auth";
import useCountListDown from "../../hooks/useCountListDown";
import { getMinute } from "../../utils/getMinute";
import dayjs from "dayjs";
import CircularProgress from "../CircularProgress";
import cn from "../../utils/cn";

function MainExer() {
  const {
    basicProgram,
    startTime,
    sendSetStep,
    step,
    emitter,
    index,
    sendCurrentIndex,
  } = useAuthContext();

  const videoRef = React.useRef<HTMLVideoElement>(null);
  const timeTable =
    basicProgram?.mainExers.flatMap((_) => [
      getMinute(_.time),
      getMinute(_.restTime),
    ]) ?? [];
  const { count, start, currentIndex, stop } = useCountListDown(
    timeTable,
    useCallback(() => {
      sendSetStep(step + 1);
      sendCurrentIndex(0);
    }, [step, sendSetStep, sendCurrentIndex]),
    true,
    true,
    index
  );

  useEffect(() => {
    setTimeout(() => {
      start();
      videoRef.current?.play();
    }, startTime.current - dayjs().valueOf());

    emitter.current.on("resume", (startTime) => {
      setTimeout(() => {
        start();
        console.log("resume");
        videoRef.current?.play();
      }, startTime - dayjs().valueOf());
    });
    emitter.current.on("pause", (startTime) => {
      setTimeout(() => {
        stop();
        console.log("pause");
        videoRef.current?.pause();
      }, startTime - dayjs().valueOf());
    });
    return () => {
      emitter.current.off("resume");
      emitter.current.off("pause");
    };
  }, []);

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex w-full pl-[70px] pr-[40px] justify-between items-center bg-bg h-[300px]">
        <p className="text-[64px] font-bold text-white">Main Exer</p>
        <div className="w-[270px]">
          <CircularProgress
            maxValue={timeTable[currentIndex] ?? 0}
            value={count}
            color={currentIndex % 2 === 0 ? undefined : "#FF5757"}
          />
        </div>
      </div>
      <div className="flex-1 bg-white relative overflow-hidden">
        <div className="absolute inset-0 transition-transform duration-700 ease-out"
             style={{ transform: `translateY(-${Math.ceil(currentIndex / 2) * 50}%)` }}>
          {basicProgram?.mainExers.map((exer, idx) => {
            const currentIdx = Math.ceil(currentIndex / 2);
            const isVisible = Math.abs(idx - currentIdx) <= 2; // 현재 기준 앞뒤 2개만 렌더링
            
            if (!isVisible) return <div key={idx} className="h-1/2" />; // 빈 공간 유지
            
            return (
              <div key={idx} className="h-1/2 relative">
                {exer.movement.direction !== "X" && (
                  <div
                    className={cn(
                      "absolute top-[30px] z-10",
                      exer.movement.direction === "L"
                        ? "left-[30px]"
                        : "right-[30px]"
                    )}
                  >
                    <p className={cn(
                      "text-[96px] font-medium text-black transition-opacity duration-300",
                      idx === currentIdx ? "opacity-100" : "opacity-50"
                    )}>
                      {exer.movement.direction}
                    </p>
                  </div>
                )}
                <video
                  className={cn(
                    "w-full h-full object-contain transition-opacity duration-300",
                    idx === currentIdx ? "opacity-100" : "opacity-70"
                  )}
                  ref={idx === currentIdx ? videoRef : null}
                  loop
                  muted
                  autoPlay
                  style={{
                    animation: currentIndex % 2 !== 0 && timeTable[currentIndex] === 0 ? 'none' : undefined
                  }}
                >
                  <source
                    src={exer.movement.video}
                    type="video/mp4"
                  />
                </video>
              </div>
            );
          })}
          {/* 마지막 빈 공간 */}
          <div className="h-1/2 relative bg-gray-50">
            <div className="w-full h-full flex items-center justify-center">
              <p className="text-2xl text-gray-400">마지막 운동입니다</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MainExer;
